import React, { useEffect, useState } from 'react';
import { shape, bool } from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import '@thd-olt-component-react/placeholder/dist/component/placeholder.style.scss';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { ProductList } from '@thd-olt-component-react/product-list';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { CustomerAccountNavigation, useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Typography } from '@one-thd/sui-atomic-components';
import { LISTS_CRUMB } from '../../constants';

export const List = ({ defaultVariables, authenticated }) => {
  declareContexts([ExperienceContext, CustomerContext]);
  const [isClient, setIsClient] = useState(false);
  const { listId } = useParams();
  const params = new URLSearchParams(useLocation().search);
  const pageParam = params.get('page') || 1;
  const page = +pageParam;
  const navigate = useNavigate();
  const location = useLocation();
  const [analyticsFired, setAnalyticsFired] = useState(false);
  const [analyticsOutput, setAnalyticsOutput] = useState({});

  const handleBackClick = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('page');
    navigate({
      pathname: '/list/view/summary',
      search: searchParams.toString()
    });
  };

  const handlePageChange = ({ page: newPage }) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', newPage);
    navigate({
      ...location,
      search: searchParams.toString()
    });
  };

  const listCrumbs = useAccountCrumbs([
    LISTS_CRUMB,
    {
      path: location.pathname,
      label: 'List Details',
    },
  ]).map((crumb) => {
    return {
      label: crumb.label,
      url: crumb.path || crumb.url,
    };
  });

  // Build hierarchical page names and categories for analytics from breadcrumbs
  // Filter out "Home" and start building categories from the first non-Home crumb
  const filteredCrumbs = listCrumbs
    .filter((crumb) => crumb.label.toLowerCase() !== 'home')
    .map((crumb) => crumb.label.toLowerCase());
  // Build analyticsCategories from filteredCrumbs
  const analyticsCategories = {};
  filteredCrumbs.forEach((crumb, index) => {
    if (index === 0) {
      analyticsCategories.primaryCategory = crumb;
    } else {
      const prevCategory = index === 1
        ? analyticsCategories.primaryCategory
        : analyticsCategories[`subCategory${index - 1}`];
      analyticsCategories[`subCategory${index}`] = `${prevCategory}>${crumb}`;
    }
  });
  const pageName = filteredCrumbs.join('>');

  // Handler to receive analytics data from ProductList and other components
  const handleAnalyticsData = (data) => {
    setAnalyticsOutput((prev) => {
      const merged = { ...prev, ...data };
      // Only fire analytics once per page load
      if (!analyticsFired && merged.myList) {
        // Add page information to the analytics payload
        const analyticsPayload = {
          ...merged,
          page: {
            pageInfo: {
              pageName,
            },
            category: {
              ...analyticsCategories
            }
          }
        };
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-details.page-load', analyticsPayload);
        setAnalyticsFired(true);
      }
      return merged;
    });
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  const productListWrapperClass = classNames({
    'sui-col-span-12 lg:sui-col-span-9 sui-pr-4': authenticated || !isClient,
    'sui-col-span-12 sui-px-4': (!authenticated && isClient)
  });

  return (
    <div className="sui-max-w-screen-2xl sui-mx-auto">
      <div className="sui-pl-4 sui-pt-4">
        <ErrorBoundary name="list-summary-breadcrumbs">
          <Breadcrumbs staticCrumbs={listCrumbs} />
        </ErrorBoundary>
        <Typography variant="h1" weight="display">
          LISTS
        </Typography>
      </div>
      <div className="sui-grid sui-grid-cols-12">
        <div className="lg:sui-col-span-3 sui-col-span-12 lg:sui-pl-3 lg:sui-pt-8">
          <ErrorBoundary name="list-summary-left-hand-nav">
            {
              authenticated && (
                <CustomerAccountNavigation />
              )
            }
          </ErrorBoundary>
        </div>
        <div className={productListWrapperClass}>
          <ErrorBoundary name="list-details">
            <QueryProvider defaultVariables={defaultVariables} cacheKey="list-details-desktop">
              <ProductList
                id={listId}
                onDelete={handleBackClick}
                onPageChange={handlePageChange}
                page={page}
                onAnalyticsData={handleAnalyticsData}
              />
            </QueryProvider>
          </ErrorBoundary>
        </div>
      </div>
      <ErrorBoundary name="list-details-back-to-top">
        <BackToTop />
      </ErrorBoundary>
    </div>
  );
};

List.propTypes = {
  defaultVariables: shape({}),
  authenticated: bool,
};

List.defaultProps = {
  defaultVariables: {},
  authenticated: false,
};

List.displayName = 'MyListPage';

List.dataModel = extend(
  ProductList,
  Breadcrumbs,
  CustomerAccountNavigation
);
